/* ------------------------------------------------------------------
   ml-finder.js
   The Find Your Store search box.

   The whole point of this page is one job: a person types the name of
   the company they work for and lands on that company's store. So the
   search runs against a generated index of the real store collections
   (assets/ml-stores-1.js and its siblings), not against the
   storefront's collection search, which also returns Nike, Polos and
   the master catalog.

   The index is loaded lazily, on the first focus or the first
   keystroke, so a visitor who never uses the box never downloads it.
   Once loaded it lives in memory, so every keystroke after that is a
   local scan of a few thousand short strings, which is instant and
   never shows a spinner.

   No dependencies, no build step, and it runs twice safely: the
   initialiser marks each root it has wired.
   ------------------------------------------------------------------ */
(function () {
  'use strict';

  var INDEX = null;      // prepared rows, once
  var LOADING = null;    // in-flight promise, shared by every box

  /* ---------- index loading -------------------------------------- */

  /* Injected as scripts rather than fetched: they are theme assets, so
     Shopify versions each URL and the browser caches them the way it
     caches the stylesheet.

     The index ships as several parts because the Admin API will not
     take one file that size in a single write. Each part pushes its
     own block of lines onto window.MLStores, and the order the parts
     land in does not matter: results are ranked by score, never by
     position in the file. */
  function loadIndex(urls) {
    if (INDEX) return Promise.resolve(INDEX);
    if (LOADING) return LOADING;

    LOADING = Promise.all(urls.map(function (url) {
      return new Promise(function (resolve, reject) {
        var s = document.createElement('script');
        s.src = url;
        s.async = true;
        s.onload = resolve;
        s.onerror = function () { reject(new Error('index part failed: ' + url)); };
        document.head.appendChild(s);
      });
    })).then(function () {
      var parts = window.MLStores || [];
      if (!parts.length) throw new Error('index empty');
      INDEX = prepare(parts.join('\n'));
      return INDEX;
    });

    return LOADING;
  }

  /* One store per line: Title|handle|alt.

     The handle is left out when it is only the title lowercased with
     the punctuation removed, which is true of nearly nine in ten of
     them and takes a third off the file. Alt carries the collection's
     tag when it says something the title does not, which is how "RCH"
     finds Retirement Clearinghouse and "labor.ny.gov" finds the New
     York State Department of Labor. */
  function prepare(text) {
    var lines = text.split('\n');
    var out = [];
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i];
      if (!line) continue;
      var f = line.split('|');
      out.push(row(f[1] || compact(f[0]), f[0], f[2], null));
    }
    return out;
  }

  function row(handle, title, alt, url) {
    var words = splitWords(title + ' ' + (alt || ''));
    return {
      h: handle,
      t: title,
      u: url || null,                  // set for stores on their own domain
      w: words,                        // ['retirement','clearinghouse','rch']
      /* Two compact forms, because the alt text has to be searchable
         without spoiling an exact match: someone who types Suncoast in
         full must get the store actually called Suncoast, not the one
         whose tag happens to contain the word. So the name alone
         decides exact and starts-with, and the name plus the alt
         decides contains. */
      n: compact(title),
      c: words.join(''),
      i: initials(words)               // 'rc'
    };
  }

  function href(r) {
    return r.u ? r.u : '/collections/' + encodeURIComponent(r.h);
  }

  /* Stores that live on their own domain are not collections on this
     shop, so they cannot come out of the generated index. They are
     typed into the section as "Name = https://..." lines instead. */
  function extras(text) {
    var out = [];
    (text || '').split('\n').forEach(function (line) {
      var at = line.indexOf('=');
      if (at < 1) return;
      var name = line.slice(0, at).trim();
      var url = line.slice(at + 1).trim();
      if (name && url) out.push(row('', name, '', url));
    });
    return out;
  }

  /* Company names on this store arrive in every shape there is:
     "Coldstream", "ShotGunLawEnforcementMC", "10-8 Systems",
     "labor.ny.gov". Splitting on case changes as well as on
     punctuation is what lets someone type "law enforcement" and find
     the first one. */
  function splitWords(s) {
    return s
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter(Boolean);
  }

  function initials(words) {
    var s = '';
    for (var i = 0; i < words.length; i++) s += words[i].charAt(0);
    return s;
  }

  function compact(s) {
    return s.toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  /* ---------- scoring -------------------------------------------- */

  /* Ranked rather than filtered, because "sun" should put Sun Valley
     Electric above Rising Sun Consulting rather than above or below it
     by luck of the alphabet.

     Exact name, then name-starts-with, then word-starts-with, then
     anywhere in the name, then the acronym, and only if all of that
     comes back empty, a loose subsequence pass that survives a typo. */
  function score(row, q, qWords) {
    var s = 0;

    if (row.n === q) return 1000;
    if (row.n.indexOf(q) === 0) s = 800 - Math.min(row.n.length - q.length, 90);
    else {
      for (var i = 0; i < row.w.length; i++) {
        if (row.w[i].indexOf(q) === 0) { s = 620 - Math.min(i * 12, 90); break; }
      }
      if (!s && row.c.indexOf(q) > -1) s = 420;
    }

    /* Multi word queries: every word the person typed has to start a
       word in the name. "budd lake" finds BuddLakeAssociation. */
    if (!s && qWords.length > 1) {
      var all = true;
      for (var j = 0; j < qWords.length; j++) {
        var hit = false;
        for (var k = 0; k < row.w.length; k++) {
          if (row.w[k].indexOf(qWords[j]) === 0) { hit = true; break; }
        }
        if (!hit) { all = false; break; }
      }
      if (all) s = 560;
    }

    if (!s && q.length > 1 && row.i.indexOf(q) === 0) s = 520;

    return s;
  }

  /* Do the letters appear in order, allowing gaps. Only used when a
     strict pass found nothing, so "coldstrm" still lands.

     Two guards, because a bare subsequence test matches almost
     anything: the first letter has to be right, and the name cannot be
     much longer than what was typed. Without them "coldstrm" also
     returns Antietam School District, which reads as the search being
     broken rather than forgiving. */
  function loose(hay, q) {
    if (hay.charAt(0) !== q.charAt(0)) return false;
    if (hay.length > q.length * 2) return false;
    var i = 0;
    for (var c = 0; c < hay.length && i < q.length; c++) {
      if (hay.charAt(c) === q.charAt(i)) i++;
    }
    return i === q.length;
  }

  function search(rows, raw, limit) {
    var q = compact(raw);
    if (!q) return [];
    var qWords = splitWords(raw);
    var hits = [];
    var i, s;

    for (i = 0; i < rows.length; i++) {
      s = score(rows[i], q, qWords);
      if (s) hits.push([s, rows[i]]);
    }

    if (!hits.length && q.length > 3) {
      for (i = 0; i < rows.length; i++) {
        if (loose(rows[i].n, q)) hits.push([120, rows[i]]);
      }
    }

    hits.sort(function (a, b) {
      if (b[0] !== a[0]) return b[0] - a[0];
      if (a[1].n.length !== b[1].n.length) return a[1].n.length - b[1].n.length;
      return a[1].t < b[1].t ? -1 : 1;
    });

    return hits.slice(0, limit).map(function (x) { return x[1]; });
  }

  /* ---------- rendering ------------------------------------------ */

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* The matched run is marked in the rendered name so it is obvious
     why a row is in the list. Marking is done on the displayed title,
     which is not the string that was matched, so it is best effort:
     find the run, or mark nothing. */
  function markup(title, raw) {
    var q = raw.trim();
    if (!q) return esc(title);
    var at = title.toLowerCase().indexOf(q.toLowerCase());
    if (at < 0) return esc(title);
    return esc(title.slice(0, at)) + '<mark>' + esc(title.slice(at, at + q.length)) +
           '</mark>' + esc(title.slice(at + q.length));
  }

  /* ---------- one search box ------------------------------------- */

  function init(root) {
    if (root.dataset.mlfReady) return;
    root.dataset.mlfReady = '1';

    var input = root.querySelector('.mlf-input');
    var list = root.querySelector('.mlf-results');
    var status = root.querySelector('.mlf-status');
    var clear = root.querySelector('.mlf-clear');
    var help = root.querySelector('.mlf-miss');
    var count = root.querySelector('.mlf-count');
    var urls = (root.dataset.index || '').split(',').filter(Boolean);
    var min = parseInt(root.dataset.min, 10) || 2;
    var limit = parseInt(root.dataset.limit, 10) || 8;
    var rowsNow = [];
    var active = -1;
    var extra = extras(root.dataset.extra);
    var carried = '';   // what we last put in the form's Company field

    function setStatus(text, kind) {
      if (!status) return;
      status.textContent = text || '';
      status.className = 'mlf-status' + (kind ? ' ' + kind : '');
      status.hidden = !text;
    }

    function showMiss(show, term) {
      if (!help) return;
      help.hidden = !show;
      if (show && term) {
        var slot = help.querySelector('[data-term]');
        if (slot) slot.textContent = term;
        /* Hand the typed name to the form further down the page, so a
           person who could not find their store does not type it a
           second time. Only ever overwrite our own earlier guess: if
           the visitor has typed something into that field themselves,
           it stays. */
        var company = document.getElementById('ml-company');
        if (company && (!company.value || company.value === carried)) {
          company.value = term;
          carried = term;
        }
      }
    }

    function paint(rows, raw) {
      rowsNow = rows;
      active = -1;
      list.innerHTML = rows.map(function (r, n) {
        return '<li role="option" id="mlf-o' + n + '" aria-selected="false">' +
                 '<a href="' + esc(href(r)) + '">' +
                   '<span class="nm">' + markup(r.t, raw) + '</span>' +
                   '<span class="go" aria-hidden="true">' +
                     '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" ' +
                     'stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
                     '<line x1="5" y1="12" x2="19" y2="12"></line>' +
                     '<polyline points="12 5 19 12 12 19"></polyline></svg>' +
                   '</span>' +
                 '</a>' +
               '</li>';
      }).join('');
      list.hidden = !rows.length;
      input.setAttribute('aria-expanded', rows.length ? 'true' : 'false');
      input.removeAttribute('aria-activedescendant');
    }

    function highlight(n) {
      var items = list.querySelectorAll('li');
      for (var i = 0; i < items.length; i++) {
        var on = i === n;
        items[i].classList.toggle('on', on);
        items[i].setAttribute('aria-selected', on ? 'true' : 'false');
      }
      active = n;
      if (n > -1) {
        input.setAttribute('aria-activedescendant', 'mlf-o' + n);
        items[n].scrollIntoView({ block: 'nearest' });
      } else {
        input.removeAttribute('aria-activedescendant');
      }
    }

    function run() {
      var raw = input.value.trim();
      clear.hidden = !raw;

      if (raw.length < min) {
        paint([], raw);
        showMiss(false);
        setStatus(raw.length ? root.dataset.msgMore || '' : '');
        return;
      }

      if (!INDEX) {
        setStatus(root.dataset.msgLoading || 'Loading stores...');
        pull().then(run);
        return;
      }

      var rows = search(extra.concat(INDEX), raw, limit);
      paint(rows, raw);

      if (rows.length) {
        setStatus('');
        showMiss(false);
      } else {
        setStatus('');
        showMiss(true, raw);
      }
    }

    function pull() {
      return loadIndex(urls).then(function (rows) {
        setStatus('');
        if (count && rows.length) {
          count.textContent = (root.dataset.countPre || '') + (rows.length + extra.length).toLocaleString() +
                              (root.dataset.countPost || '');
          count.hidden = false;
        }
        return rows;
      }).catch(function () {
        setStatus(root.dataset.msgError ||
          'The store list could not be loaded. Please use the form below and we will send you your link.', 'err');
        return [];
      });
    }

    input.addEventListener('focus', function () { if (!INDEX) pull(); }, { once: true });
    input.addEventListener('input', run);

    input.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (!rowsNow.length) return;
        e.preventDefault();
        var next = e.key === 'ArrowDown' ? active + 1 : active - 1;
        if (next >= rowsNow.length) next = 0;
        if (next < 0) next = rowsNow.length - 1;
        highlight(next);
      } else if (e.key === 'Enter') {
        /* Enter with nothing chosen goes to the top result, which is
           what a person who typed their whole company name expects. */
        if (rowsNow.length) {
          e.preventDefault();
          var pick = active > -1 ? active : 0;
          window.location.href = href(rowsNow[pick]);
        }
      } else if (e.key === 'Escape') {
        if (rowsNow.length) { paint([], ''); }
        else { input.value = ''; run(); }
      }
    });

    clear.addEventListener('click', function () {
      input.value = '';
      run();
      input.focus();
    });

    /* Clicking away closes the list but keeps what was typed, so the
       box can be reopened by typing one more character. */
    document.addEventListener('click', function (e) {
      if (!root.contains(e.target) && rowsNow.length) paint([], input.value);
    });

    root.querySelector('form').addEventListener('submit', function (e) {
      e.preventDefault();
      if (rowsNow.length) window.location.href = href(rowsNow[0]);
      else run();
    });

    /* ?q=coldstream, so a link in an email or a support reply can put
       someone straight on their result. */
    var pre = new URLSearchParams(window.location.search).get('q');
    if (pre) {
      input.value = pre;
      pull().then(run);
    }
  }

  function boot() {
    var roots = document.querySelectorAll('[data-mlf]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', boot)
    : boot();

  /* The theme editor re-renders a section without reloading the page. */
  document.addEventListener('shopify:section:load', boot);
})();
